import {
  CollectionSubType,
  type EntitySubtype,
  EntityType,
  ModuleType,
  ShowSubtype,
  TagSubtype,
} from '@audacy-clients/client-services/core';
import { EntityImageShape, type IModule } from '@audacy-clients/core/atoms/wrappers/modules';
import type { IContentSummary } from '@audacy-clients/core/atoms/wrappers/types';
import type { IModuleContext } from '@audacy-clients/core/components/ModuleRenderer/types';
import type { IPlayContext } from '@audacy-clients/core/hooks/useAudioHelpers';
import { getPlayingUntilFromStartAndDuration } from '@audacy-clients/core/utils/date';
import { getEpisodeTitleAndImage } from '@audacy-clients/core/utils/episode';

import { CardType, type ICard, type TModuleFeaturedCardProps } from '~/components/Card';
import i18n from '~/i18n';
import { isPlayable } from '~/utils/card';
import { formatPublishDate } from '~/utils/date';

const CardTypeMapping: { [Key in ModuleType]?: CardType } = {
  [ModuleType.ENTITY_CARD_FEATURED]: CardType.Featured,
  [ModuleType.ENTITY_CARD_HORIZONTAL]: CardType.Horizontal,
  [ModuleType.ENTITY_CARD_IMAGE_GROUP]: CardType.Vertical,
  [ModuleType.ENTITY_CARD_VERTICAL]: CardType.Vertical,
  [ModuleType.COLLECTION_PLAYLIST_CARD]: CardType.Vertical,
};

export const getEntityLabel = (
  eyebrowEntityType: EntityType | undefined,
  eyebrowEntitySubtype: EntitySubtype | undefined,
  { isLowercase }: { isLowercase?: boolean } = {},
): string => {
  const translationCapsKey = isLowercase ? 'lowercase' : 'capitalized';
  switch (eyebrowEntityType) {
    case EntityType.STATION:
      return i18n.t(`entity.entityLabel.station.${translationCapsKey}`);
    case EntityType.COLLECTION:
      return i18n.t(`entity.entityLabel.collection.playlist.${translationCapsKey}`);
    case EntityType.SHOW:
      switch (eyebrowEntitySubtype) {
        case ShowSubtype.PODCAST:
          return i18n.t(`entity.entityLabel.podcast.${translationCapsKey}`);
        default:
          return i18n.t(`entity.entityLabel.show.${translationCapsKey}`);
      }
    case EntityType.PLAYLIST: {
      const isPodcastEntity = eyebrowEntitySubtype?.toLowerCase().includes('podcast');
      return isPodcastEntity
        ? i18n.t(`entity.entityLabel.podcast.${translationCapsKey}`)
        : i18n.t(`entity.entityLabel.show.${translationCapsKey}`);
    }
    case EntityType.TAG:
      switch (eyebrowEntitySubtype) {
        case TagSubtype.GENRE:
          return i18n.t('entity.genreAndEntityLabel', {
            genre: eyebrowEntitySubtype,
          });
        default:
          return i18n.t(`entity.entityLabel.topic.${translationCapsKey}`);
      }
    default:
      return '';
  }
};

export const getCardType = (
  moduleType: ModuleType,
  moduleContext: IModuleContext,
): CardType | undefined => {
  if (moduleContext.carousel?.isStacked) {
    return CardType.Horizontal;
  }

  return CardTypeMapping[moduleType];
};

export const isStacked = (module: IModule): boolean => !!module?.config?.itemsPerColumn;

export const formatFeaturedCard = (
  type: CardType,
  module: IModule,
  playContext: IPlayContext,
  data?: IContentSummary,
  context?: IModuleContext,
): TModuleFeaturedCardProps => {
  const eyebrow = module.config?.hideEyebrow
    ? undefined
    : module.config?.eyebrow
      ? module.config.eyebrow
      : data?.publishDate
        ? [`${formatPublishDate(data.publishDate)}`, data.parentTitle || ''].filter(
            (item) => !!item,
          )
        : data?.parentTitle || data?.title;
  const episodeThumbnail = data?.entityType === EntityType.EPISODE ? data?.parentImage : undefined;
  const thumbnail =
    module.config.thumbnail && !module.config.hideThumbnail ? module.config.thumbnail : undefined;

  return {
    contentId: data?.id,
    ctaHref: module.config.url,
    duration: data?.duration,
    entitySubtype: data?.entitySubtype,
    entityType: data?.entityType,
    entityURL: data?.url,
    eyebrow,
    forceVertical: !!context?.carousel && context?.carousel.numberOfItems > 1,
    hideThumbnail: module.config.hideThumbnail,
    highlightText: module.config.highlight,
    imageAltText: module.config.imageAlt,
    imageURIBase: module.config.image,
    isExternalUrl: module.config.isExternalUrl,
    isPlayable: data?.entityType ? isPlayable(data.entityType) : false,
    linkText: module.config.ctaText,
    playContext,
    replayableUntilDateTime: data?.replayableUntilDateTime,
    subtitle: module.config.subtitle,
    thumbnailURI: thumbnail || episodeThumbnail || data?.image,
    title: (module.config.title as string) || '',
    type,
  };
};

export const formatCard = (
  type: CardType,
  module: IModule,
  playContext: IPlayContext,
  data?: IContentSummary,
  context?: IModuleContext,
): ICard => {
  const href = data?.url;
  const showHref = data?.showUrl;
  const duration =
    data?.entitySubtype === CollectionSubType.PLAYLIST && module.config?.totalDurationInSeconds
      ? +module.config.totalDurationInSeconds
      : data?.duration;

  const isFixed =
    type === CardType.Horizontal && !!context?.carousel && !context.carousel.isStacked;

  const [title, image] = getEpisodeTitleAndImage(data);

  return {
    contentId: data?.id,
    duration,
    isPlayable: data?.entityType ? isPlayable(data.entityType) : false,
    liveUntil: {
      playingUntil: getPlayingUntilFromStartAndDuration(data?.startDateTime, data?.duration),
    },
    publishDate: data?.publishDate,
    entityType: data?.entityType,
    entitySubtype: data?.entitySubtype,
    href,
    episodeHref: data?.episodeUrl,
    showHref,
    image,
    imageAlt: title,
    imageShape: module.config.entityImageShape || EntityImageShape.Square,
    showContentId: data?.showContentId,
    startDateTime: data?.startDateTime,
    title,
    type,
    isFixed,
    playContext,
    parentTitle: data?.parentTitle,
    genres: data?.genres,
    marketTitle: data?.marketTitle,
    author: data?.author,
    itemsCount: module.config?.idList?.length,
    replayableUntilDateTime: data?.replayableUntilDateTime,
  };
};
